import api from './index'

interface ScriptRebuttalInterface {
  slug: string
  contents: APIScriptContentInterface[]
}

interface APIScriptContentInterface {
  disabled: boolean
  order: number
  ordinal: number
  content: string
  title: string
  id: string
  slug: string
}

interface ScriptContentInterface {
  disabled: boolean
  order: number
  fileContent: string
  title: string
  id: number | string
  slug: string
}

export async function getScriptsAndRebuttalsObject(
  token: string,
): Promise<{ APIScriptsCopy: object; APIRebuttalsCopy: object }> {
  try {
    const res = await api.get('/agent/rebuttals', {
      headers: { Authorization: `Bearer ${token}` },
    })

    if (res.status !== 200) {
      throw new Error(`Non-200 status: ${res.status}`)
    }

    const allRebuttals =
      res.data.data.rebuttals ??
      ([] as Record<string, APIScriptContentInterface>[])
    const allScripts =
      res.data.data.scripts ??
      ([] as Record<string, APIScriptContentInterface>[])

    const returnScripts = {} as Record<
      string,
      Record<string, ScriptContentInterface>
    >
    const returnRebuttals = {} as Record<
      string,
      Record<string, ScriptContentInterface>
    >

    allScripts.forEach((script: ScriptRebuttalInterface) => {
      returnScripts[script.slug] = {}
      script.contents?.forEach((c: APIScriptContentInterface) => {
        returnScripts[script.slug][c.title] = {
          disabled: c.disabled,
          order: c.ordinal,
          fileContent: c.content,
          title: c.title,
          id: c.id,
          slug: c.slug,
        }
      })
    })

    allRebuttals.forEach((reb: ScriptRebuttalInterface) => {
      returnRebuttals[reb.slug] = {}
      reb.contents?.forEach((c: APIScriptContentInterface) => {
        returnRebuttals[reb.slug][c.title] = {
          disabled: c.disabled,
          order: c.ordinal,
          fileContent: c.content,
          title: c.title,
          id: c.id,
          slug: c.slug,
        }
      })
    })

    return { APIScriptsCopy: returnScripts, APIRebuttalsCopy: returnRebuttals }
  } catch (err) {
    console.error('getScriptsAndRebuttalsObject failed:', err)
    return { APIScriptsCopy: {}, APIRebuttalsCopy: {} }
  }
}

export async function getRebuttalHash(token: string): Promise<string> {
  const checksumPromise = await api
    .get('/agent/rebuttals/checksum', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .catch(() => {
      console.warn('Error communicating with module-manager api')
    })

  if (checksumPromise && checksumPromise.status === 200) {
    return checksumPromise.data.data.checksum
  }

  return ''
}

export async function updateScriptAPICall(
  isScripts: boolean,
  token: string,
  contentSlug: string,
  scriptName: string,
  objectSlug: string,
  updatedText: string,
): Promise<void> {
  await api
    .put(
      `/agent/${isScripts ? 'scripts' : 'rebuttals'}/${objectSlug}/content/${contentSlug}`,
      {
        name: scriptName,
        slug: objectSlug,
        content: updatedText,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    .catch(() => {
      console.warn('Error communicating with module-manager api (Update)')
    })
}

export async function getScriptRebuttalHistory(
  isScripts: boolean = true,
  token: string | null,
  objectSlug: string = 'en_newhire_payer_ndr',
  scriptName: string = 'page-1',
) {
  //en_newhire_payer_ndr
  const historyPromise = await api
    .get(
      `/agent/${isScripts ? 'scripts' : 'rebuttals'}/${objectSlug}/content/${scriptName}?include=history`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    .catch((err) => {
      console.error(err)
    })

  if (historyPromise && historyPromise.status == 200) {
    return historyPromise.data.data.history
  }

  return {}
}
